import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

const LS_ACQUEREURS = "yoma_suivi_ventes";
const LS_COMPANY = "yoma_company_name";
const LS_SELECTED_PROJECT = "yoma_selected_projet_promotion";

const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const BG = "#F8F4EE";
const BORDER = "#E3D7C8";
const WHITE = "#FFFFFF";
const GREEN = "#5C8A5C";
const ORANGE = "#C09A5B";
const RED = "#B85C3A";
const GRIS = "#9A8778";

const FF_PLAYFAIR = "'Playfair Display', Georgia, serif";
const FF_DM = "'DM Sans', system-ui, sans-serif";

const DEFAULT_PCTS = [30, 25, 25, 15, 5];

const STATUTS = [
  { value: "integral", label: "Intégral" },
  { value: "partiel", label: "Partiel" },
  { value: "non_paye", label: "Non payé" },
  { value: "a_venir", label: "À venir" },
];

const STATUT_COLOR = {
  integral: GREEN,
  partiel: ORANGE,
  non_paye: RED,
  a_venir: GRIS,
};

function readLs(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeLs(storageKey, data) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

function formatMad(n) {
  if (n === "" || n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} MAD`;
}

function montantPrevu(prixVente, pct) {
  const p = Number(prixVente) || 0;
  const c = Number(pct) || 0;
  return (p * c) / 100;
}

function createVersements(pcts) {
  return pcts.map((pct, i) => ({
    id: `v${i + 1}`,
    pct: Number(pct) || 0,
    statut: "a_venir",
    montantVerse: 0,
    dateVersement: "",
    dateEcheance: "",
    notes: "",
  }));
}

function createAcquereur(pcts = DEFAULT_PCTS) {
  return {
    id: `acq-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    nom: "",
    prenom: "",
    numVilla: "",
    prixVente: "",
    email: "",
    versements: createVersements(pcts),
  };
}

function isRetard(v, montantPrev) {
  if (v.statut === "integral" || v.statut === "a_venir") return false;
  if (v.statut === "non_paye") return true;
  if (v.dateEcheance) {
    const d = new Date(v.dateEcheance);
    d.setHours(0, 0, 0, 0);
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    if (d < t && v.statut !== "integral") return true;
  }
  if (v.statut === "partiel" && Number(v.montantVerse) < montantPrev - 0.01) {
    if (v.dateEcheance) {
      const d = new Date(v.dateEcheance);
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      d.setHours(0, 0, 0, 0);
      return d < t;
    }
    return false;
  }
  return false;
}

export default function SuiviVentesPage({ cabinetId = null }) {
  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: TAUPE }}>Chargement...</div>;
  }

  const lsCompanyKey = `${LS_COMPANY}_${cabinetId}`;
  const lsSelectedProjectKey = `${LS_SELECTED_PROJECT}_${cabinetId}`;

  const [cabinet, setCabinet] = useState(() =>
    typeof localStorage !== "undefined" ? localStorage.getItem(lsCompanyKey) || "YOMA DESIGN" : "YOMA DESIGN"
  );

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    try {
      return localStorage.getItem(lsSelectedProjectKey) || "";
    } catch {
      return "";
    }
  });
  const [acquereurs, setAcquereurs] = useState([]);

  const [modalCell, setModalCell] = useState(null);
  const [modalNew, setModalNew] = useState(false);
  const [editingAcqId, setEditingAcqId] = useState(null);
  const [formNew, setFormNew] = useState(() => ({
    nom: "",
    prenom: "",
    numVilla: "",
    prixVente: "",
    email: "",
    pcts: [...DEFAULT_PCTS],
  }));
  const [showModalProjet, setShowModalProjet] = useState(false);
  const [newProject, setNewProject] = useState({
    nom: "",
    localisation: "",
    nbLots: "",
    prixMoyen: "",
    dateLancement: "",
    statut: "en_cours",
  });

  useEffect(() => {
    const id = "yoma-fonts-suivi-ventes";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  const getProjectAcqKey = useCallback(
    (projectId) => `${LS_ACQUEREURS}_${cabinetId}_${projectId}`,
    [cabinetId]
  );

  const loadPromotionProjects = useCallback(async () => {
    const { data, error } = await supabase
      .from("promotion_programs")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Erreur chargement projets promotion:", error);
      alert("Impossible de charger les projets de promotion.");
      return;
    }
    const normalized = (data || []).map((row) => ({
      id: String(row.id),
      name: row.name || "",
      location: row.location || "",
      totalLots: Number(row.total_lots) || 0,
      avgLotPrice: row.prix_moyen ?? "",
      launchDate: row.date_lancement || "",
      status: row.statut || "en_cours",
    }));
    setProjects(normalized);
    setSelectedProjectId((prev) => {
      if (prev && normalized.some((p) => p.id === prev)) return prev;
      return normalized[0]?.id || "";
    });
  }, [cabinetId]);

  useEffect(() => {
    loadPromotionProjects();
  }, [loadPromotionProjects]);

  useEffect(() => {
    const targetId = selectedProjectId || projects[0]?.id;
    if (!targetId) {
      setAcquereurs([]);
      return;
    }
    const saved = readLs(getProjectAcqKey(targetId));
    if (Array.isArray(saved)) {
      setAcquereurs(saved);
      return;
    }
    setAcquereurs([]);
  }, [selectedProjectId, projects, getProjectAcqKey]);

  useEffect(() => {
    const targetId = selectedProjectId || projects[0]?.id;
    if (!targetId) return;
    writeLs(getProjectAcqKey(targetId), acquereurs);
  }, [acquereurs, selectedProjectId, projects, getProjectAcqKey]);

  useEffect(() => {
    const targetId = selectedProjectId || projects[0]?.id;
    if (!targetId) return;
    try {
      localStorage.setItem(lsSelectedProjectKey, targetId);
    } catch {
      /* ignore */
    }
  }, [selectedProjectId, projects, lsSelectedProjectKey]);

  const pcts = DEFAULT_PCTS;
  const selectedProject = projects.find((p) => p.id === selectedProjectId) || projects[0] || null;

  const projectCommercialStats = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      const arr = p.id === selectedProjectId ? acquereurs : readLs(getProjectAcqKey(p.id)) || [];
      const sold = arr.filter((a) => {
        const pv = Number(a.prixVente) || 0;
        if (pv <= 0) return false;
        const total = (a.versements || []).reduce((s, v) => s + (Number(v.montantVerse) || 0), 0);
        return total >= pv * 0.995;
      }).length;
      const totalLots = Number(p.totalLots) || 0;
      map[p.id] = totalLots > 0 ? Math.min(100, (sold / totalLots) * 100) : 0;
    });
    return map;
  }, [projects, selectedProjectId, acquereurs, getProjectAcqKey]);

  const kpis = useMemo(() => {
    let totalEncaisse = 0;
    let totalPrix = 0;
    let totalRetard = 0;

    acquereurs.forEach((a) => {
      const pv = Number(a.prixVente) || 0;
      totalPrix += pv;
      a.versements?.forEach((v, idx) => {
        const prev = montantPrevu(pv, v.pct ?? pcts[idx]);
        totalEncaisse += Number(v.montantVerse) || 0;
        if (isRetard(v, prev)) totalRetard += 1;
      });
    });

    const reste = Math.max(0, totalPrix - totalEncaisse);

    return {
      nbAcquereurs: acquereurs.length,
      totalEncaisse,
      reste,
      totalRetard,
    };
  }, [acquereurs, pcts]);

  const updateVersement = useCallback((acqId, vIndex, patch) => {
    setAcquereurs((prev) =>
      prev.map((a) => {
        if (a.id !== acqId) return a;
        const versements = a.versements.map((v, i) => (i === vIndex ? { ...v, ...patch } : v));
        return { ...a, versements };
      })
    );
  }, []);

  const openCellModal = (acq, vIndex) => {
    setModalCell({ acq: JSON.parse(JSON.stringify(acq)), vIndex });
  };

  const totalsRow = useMemo(() => {
    const sums = pcts.map((_, vi) =>
      acquereurs.reduce((s, a) => s + (Number(a.versements?.[vi]?.montantVerse) || 0), 0)
    );
    const totalVersé = sums.reduce((a, b) => a + b, 0);
    const totalPrix = acquereurs.reduce((s, a) => s + (Number(a.prixVente) || 0), 0);
    return { sums, totalVersé, totalPrix, resteGlobal: Math.max(0, totalPrix - totalVersé) };
  }, [acquereurs, pcts]);

  const resetFormAcquereur = () => {
    setFormNew({
      nom: "",
      prenom: "",
      numVilla: "",
      prixVente: "",
      email: "",
      pcts: [...DEFAULT_PCTS],
    });
    setEditingAcqId(null);
  };

  const openNouvelAcquereur = () => {
    resetFormAcquereur();
    setModalNew(true);
  };

  const openModifierAcquereur = (a) => {
    const pctsFrom = Array.isArray(a.versements) && a.versements.length === 5
      ? a.versements.map((v) => String(v.pct ?? ""))
      : [...DEFAULT_PCTS].map(String);
    setFormNew({
      nom: a.nom || "",
      prenom: a.prenom || "",
      numVilla: a.numVilla || "",
      prixVente: a.prixVente ?? "",
      email: a.email || "",
      pcts: pctsFrom,
    });
    setEditingAcqId(a.id);
    setModalNew(true);
  };

  const handleSubmitAcquereur = () => {
    const pctsForm = formNew.pcts.length === 5 ? formNew.pcts : [...DEFAULT_PCTS];
    const sumPct = pctsForm.reduce((s, x) => s + (Number(x) || 0), 0);
    if (Math.abs(sumPct - 100) > 0.01) {
      alert("La somme des pourcentages des versements doit être égale à 100 %.");
      return;
    }
    if (editingAcqId) {
      setAcquereurs((prev) =>
        prev.map((a) => {
          if (a.id !== editingAcqId) return a;
          const versements = (a.versements || []).map((v, i) => ({
            ...v,
            pct: Number(pctsForm[i]) || 0,
          }));
          return {
            ...a,
            nom: formNew.nom.trim(),
            prenom: formNew.prenom.trim(),
            numVilla: formNew.numVilla.trim(),
            prixVente: formNew.prixVente,
            email: formNew.email.trim(),
            versements,
          };
        })
      );
      setModalNew(false);
      resetFormAcquereur();
      return;
    }
    const a = createAcquereur(pctsForm);
    a.nom = formNew.nom.trim();
    a.prenom = formNew.prenom.trim();
    a.numVilla = formNew.numVilla.trim();
    a.prixVente = formNew.prixVente;
    a.email = formNew.email.trim();
    setAcquereurs((prev) => [...prev, a]);
    setModalNew(false);
    resetFormAcquereur();
  };

  const supprimerAcquereur = (a) => {
    if (!window.confirm(`Supprimer l'acquéreur ${a.prenom || ""} ${a.nom || ""} (Villa ${a.numVilla || "—"}) ?`)) return;
    setAcquereurs((prev) => prev.filter((x) => x.id !== a.id));
  };

  const exportPdf = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const proj = selectedProject?.name || "—";
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Suivi des ventes</title>
      <style>
        body { font-family: Georgia, serif; color: ${TAUPE}; padding: 24px; }
        h1 { font-size: 22px; }
        table { border-collapse: collapse; width: 100%; font-size: 11px; }
        th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
        th { background: #f5f0e8; }
        @media print { body { padding: 12mm; } }
      </style></head><body>
      <h1>Suivi des ventes — ${proj}</h1>
      <p>${cabinet}</p>
      <p>Total acquéreurs : ${kpis.nbAcquereurs} · Encaissé : ${formatMad(kpis.totalEncaisse)} · Reste : ${formatMad(kpis.reste)}</p>
      <table><thead><tr>
        <th>N°</th><th>Nom</th><th>Prénom</th><th>Villa</th><th>Prix</th>
        ${pcts.map((p, i) => `<th>V${i + 1} ${p}%</th>`).join("")}
        <th>Total versé</th><th>Reste</th>
      </tr></thead><tbody>
      ${acquereurs
        .map((a, idx) => {
          const tv = a.versements.reduce((s, v) => s + (Number(v.montantVerse) || 0), 0);
          const pv = Number(a.prixVente) || 0;
          const reste = Math.max(0, pv - tv);
          return `<tr>
            <td>${idx + 1}</td><td>${escapeHtml(a.nom)}</td><td>${escapeHtml(a.prenom)}</td><td>${escapeHtml(a.numVilla)}</td><td>${formatMad(pv)}</td>
            ${a.versements.map((v) => `<td>${formatMad(v.montantVerse)}</td>`).join("")}
            <td>${formatMad(tv)}</td><td>${formatMad(reste)}</td>
          </tr>`;
        })
        .join("")}
      </tbody></table>
      </body></html>`);
    w.document.close();
    w.onload = () => {
      w.focus();
      w.print();
    };
  };

  const createPromotionProject = () => {
    if (!newProject.nom.trim()) {
      alert("Le nom du projet est obligatoire.");
      return;
    }
    supabase
      .from("promotion_programs")
      .insert({
        cabinet_id: cabinetId,
        name: newProject.nom.trim(),
        location: newProject.localisation.trim(),
        total_lots: Number(newProject.nbLots) || 0,
        prix_moyen: newProject.prixMoyen === "" ? null : Number(newProject.prixMoyen),
        date_lancement: newProject.dateLancement || null,
        statut: newProject.statut || "en_cours",
      })
      .then(async ({ error }) => {
        if (error) {
          console.error("Erreur création projet promotion:", error);
          alert("Impossible de créer le projet.");
          return;
        }
        await loadPromotionProjects();
      });
    setShowModalProjet(false);
    setNewProject({
      nom: "",
      localisation: "",
      nbLots: "",
      prixMoyen: "",
      dateLancement: "",
      statut: "en_cours",
    });
  };

  const deletePromotionProject = async (projectId) => {
    if (!window.confirm("Supprimer ce projet de promotion ?")) return;
    const { error } = await supabase
      .from("promotion_programs")
      .delete()
      .eq("id", projectId)
      .eq("cabinet_id", cabinetId);
    if (error) {
      console.error("Erreur suppression projet promotion:", error);
      alert("Impossible de supprimer le projet.");
      return;
    }
    if (selectedProjectId === projectId) {
      setSelectedProjectId("");
      setAcquereurs([]);
    }
    await loadPromotionProjects();
  };

  const appelsPaiement = () => {
    const lines = [];
    acquereurs.forEach((a) => {
      a.versements.forEach((v, i) => {
        if (v.statut === "non_paye" || v.statut === "partiel") {
          const prev = montantPrevu(a.prixVente, v.pct ?? pcts[i]);
          lines.push(`${a.nom} ${a.prenom} — V${i + 1} (${STATUTS.find((s) => s.value === v.statut)?.label}) — ${formatMad(Number(v.montantVerse) || 0)} / ${formatMad(prev)}`);
        }
      });
    });
    alert(lines.length ? lines.join("\n") : "Aucun appel à paiement en cours (statuts partiel / non payé).");
  };

  return (
    <main style={{ minHeight: "100vh", background: BG, padding: 24, fontFamily: FF_DM, color: TAUPE }}>
      <header style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: "#8B7361", marginBottom: 6 }}>{cabinet}</div>
        <h1 style={{ margin: 0, fontFamily: FF_PLAYFAIR, fontSize: 32, fontWeight: 700 }}>Suivi des Ventes</h1>
        <p style={{ margin: "8px 0 0", fontSize: 14, color: "#8B7361" }}>
          Tableau de bord acquéreurs et versements
        </p>
      </header>

      <section style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 12, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontFamily: FF_PLAYFAIR, fontSize: 24, fontWeight: 700 }}>Projets de promotion</h2>
          <button
            type="button"
            onClick={() => {
              setShowModalProjet(true);
            }}
            style={{ padding: "10px 16px", border: "none", borderRadius: 8, background: GOLD, color: WHITE, fontWeight: 700, cursor: "pointer" }}
          >
            + Nouveau projet promotion
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          {projects.map((p) => {
            const pct = projectCommercialStats[p.id] || 0;
            const statusLabel =
              p.status === "termine" ? "Terminé" : p.status === "pause" ? "En pause" : "En cours";
            const statusColor = p.status === "termine" ? GREEN : p.status === "pause" ? ORANGE : GOLD;
            return (
              <div key={p.id} style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                  <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{p.name || "Projet"}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "4px 10px", background: `${statusColor}22`, color: statusColor }}>
                    {statusLabel}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: "#8B7361", marginBottom: 4 }}>{p.location || "Localisation non renseignée"}</div>
                <div style={{ fontSize: 13, color: "#8B7361", marginBottom: 4 }}>{Number(p.totalLots) || 0} lots/villas</div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>
                  % de commercialisation : <strong>{pct.toFixed(1)}%</strong>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedProjectId(p.id)}
                  style={{
                    padding: "8px 14px",
                    border: "none",
                    borderRadius: 8,
                    background: GOLD,
                    color: WHITE,
                    fontWeight: 700,
                    cursor: "pointer",
                    opacity: selectedProjectId === p.id ? 0.75 : 1,
                  }}
                >
                  Sélectionner
                </button>
                <button
                  type="button"
                  onClick={() => deletePromotionProject(p.id)}
                  style={{
                    marginLeft: 8,
                    padding: "8px 14px",
                    border: `1px solid rgba(184,92,58,0.4)`,
                    borderRadius: 8,
                    background: "rgba(184,92,58,0.08)",
                    color: RED,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Supprimer
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 16 }}>
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Total acquéreurs ({selectedProject?.name || "—"})</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700 }}>{kpis.nbAcquereurs}</div>
        </div>
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Total encaissé</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: GREEN }}>{formatMad(kpis.totalEncaisse)}</div>
        </div>
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Reste à encaisser</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: GOLD }}>{formatMad(kpis.reste)}</div>
        </div>
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 11, color: "#8B7361", marginBottom: 6 }}>Versements en retard</div>
          <div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: kpis.totalRetard > 0 ? RED : TAUPE }}>{kpis.totalRetard}</div>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <button
          type="button"
          onClick={openNouvelAcquereur}
          style={{
            padding: "10px 18px",
            border: "none",
            borderRadius: 8,
            background: GOLD,
            color: "#fff",
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: FF_DM,
          }}
        >
          + Nouvel acquéreur
        </button>
        <button
          type="button"
          onClick={appelsPaiement}
          style={{
            padding: "10px 18px",
            border: "none",
            borderRadius: 8,
            background: "#D68910",
            color: "#fff",
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: FF_DM,
          }}
        >
          Appels à paiement
        </button>
        <button
          type="button"
          onClick={exportPdf}
          style={{
            padding: "10px 18px",
            border: `1px solid ${BORDER}`,
            borderRadius: 8,
            background: WHITE,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: FF_DM,
          }}
        >
          Export PDF
        </button>
      </div>

      <div style={{ overflowX: "auto", borderRadius: 12, border: `1px solid ${BORDER}`, background: WHITE }}>
        <table style={{ width: "100%", minWidth: 1100, borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#FCFAF7" }}>
              <th style={th}>N°</th>
              <th style={th}>NOM</th>
              <th style={th}>PRÉNOM</th>
              <th style={th}>N° VILLA</th>
              <th style={th}>PRIX VENTE</th>
              {pcts.map((pct, i) => (
                <th key={i} style={th}>
                  V{i + 1} {pct}%
                </th>
              ))}
              <th style={th}>TOTAL VERSÉ</th>
              <th style={th}>RESTE</th>
              <th style={{ ...th, minWidth: 120 }}>%</th>
              <th style={{ ...th, minWidth: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {acquereurs.map((a, idx) => {
              const pv = Number(a.prixVente) || 0;
              const totalVersé = a.versements.reduce((s, v) => s + (Number(v.montantVerse) || 0), 0);
              const reste = Math.max(0, pv - totalVersé);
              const pctAv = pv > 0 ? Math.min(100, Math.round((totalVersé / pv) * 1000) / 10) : 0;
              return (
                <tr key={a.id}>
                  <td style={td}>{idx + 1}</td>
                  <td style={td}>{a.nom || "—"}</td>
                  <td style={td}>{a.prenom || "—"}</td>
                  <td style={td}>{a.numVilla || "—"}</td>
                  <td style={td}>{formatMad(pv)}</td>
                  {a.versements.map((v, vi) => {
                    const prev = montantPrevu(pv, v.pct ?? pcts[vi]);
                    const bg = STATUT_COLOR[v.statut] || GRIS;
                    return (
                      <td
                        key={v.id}
                        style={{
                          ...td,
                          background: `${bg}22`,
                          borderLeft: `3px solid ${bg}`,
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                        onClick={() => {
                          const fresh = acquereurs.find((x) => x.id === a.id);
                          if (fresh) openCellModal(fresh, vi);
                        }}
                        title="Cliquer pour détail"
                      >
                        {formatMad(v.montantVerse)}
                      </td>
                    );
                  })}
                  <td style={{ ...td, fontWeight: 700 }}>{formatMad(totalVersé)}</td>
                  <td style={td}>{formatMad(reste)}</td>
                  <td style={td}>
                    <div style={{ height: 8, background: "#EFE4D4", borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ width: `${pctAv}%`, height: "100%", background: GOLD }} />
                    </div>
                    <span style={{ fontSize: 11, color: "#8B7361" }}>{pctAv}%</span>
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      onClick={() => openModifierAcquereur(a)}
                      title="Modifier"
                      style={{
                        marginRight: 8,
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: `1px solid ${BORDER}`,
                        background: WHITE,
                        cursor: "pointer",
                        fontFamily: FF_DM,
                        fontSize: 12,
                        fontWeight: 600,
                        color: TAUPE,
                      }}
                    >
                      ✎ Modifier
                    </button>
                    <button
                      type="button"
                      onClick={() => supprimerAcquereur(a)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: `1px solid rgba(184,92,58,0.4)`,
                        background: "rgba(184,92,58,0.08)",
                        cursor: "pointer",
                        fontFamily: FF_DM,
                        fontSize: 12,
                        fontWeight: 600,
                        color: RED,
                      }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: "#F8F4EE", fontWeight: 700 }}>
              <td colSpan={5} style={td}>
                TOTAUX
              </td>
              {totalsRow.sums.map((s, i) => (
                <td key={i} style={td}>
                  {formatMad(s)}
                </td>
              ))}
              <td style={td}>{formatMad(totalsRow.totalVersé)}</td>
              <td style={td}>{formatMad(totalsRow.resteGlobal)}</td>
              <td style={td}>—</td>
              <td style={td} />
            </tr>
          </tfoot>
        </table>
        {acquereurs.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: "#8B7361" }}>Aucun acquéreur — ajoutez une ligne pour commencer.</div>
        )}
      </div>

      {/* Modal versement */}
      {modalCell && (
        <div
          style={overlay}
          onClick={() => setModalCell(null)}
          role="presentation"
        >
          <div
            style={modalBox}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <h3 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>
              Versement V{(modalCell.vIndex ?? 0) + 1} — {modalCell.acq.nom} {modalCell.acq.prenom}
            </h3>
            {(() => {
              const a = modalCell.acq;
              const vi = modalCell.vIndex;
              const v = { ...a.versements[vi] };
              const prev = montantPrevu(a.prixVente, v.pct ?? pcts[vi]);
              return (
                <>
                  <p style={{ fontSize: 13, color: "#8B7361" }}>
                    Montant prévu : <strong>{formatMad(prev)}</strong> ({v.pct ?? pcts[vi]}%)
                  </p>
                  <label style={lab}>Statut</label>
                  <select
                    value={v.statut}
                    onChange={(e) => {
                      const nv = { ...a.versements[vi], statut: e.target.value };
                      const arr = [...a.versements];
                      arr[vi] = nv;
                      setModalCell({ acq: { ...a, versements: arr }, vIndex: vi });
                    }}
                    style={inp}
                  >
                    {STATUTS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <label style={lab}>Montant versé réel (MAD)</label>
                  <input
                    type="number"
                    value={v.montantVerse}
                    onChange={(e) => {
                      const nv = { ...a.versements[vi], montantVerse: e.target.value };
                      const arr = [...a.versements];
                      arr[vi] = nv;
                      setModalCell({ acq: { ...a, versements: arr }, vIndex: vi });
                    }}
                    style={inp}
                  />
                  <label style={lab}>Date versement</label>
                  <input
                    type="date"
                    value={v.dateVersement}
                    onChange={(e) => {
                      const nv = { ...a.versements[vi], dateVersement: e.target.value };
                      const arr = [...a.versements];
                      arr[vi] = nv;
                      setModalCell({ acq: { ...a, versements: arr }, vIndex: vi });
                    }}
                    style={inp}
                  />
                  <label style={lab}>Date échéance (pour suivi retard)</label>
                  <input
                    type="date"
                    value={v.dateEcheance}
                    onChange={(e) => {
                      const nv = { ...a.versements[vi], dateEcheance: e.target.value };
                      const arr = [...a.versements];
                      arr[vi] = nv;
                      setModalCell({ acq: { ...a, versements: arr }, vIndex: vi });
                    }}
                    style={inp}
                  />
                  <label style={lab}>Notes</label>
                  <textarea
                    value={v.notes || ""}
                    onChange={(e) => {
                      const nv = { ...a.versements[vi], notes: e.target.value };
                      const arr = [...a.versements];
                      arr[vi] = nv;
                      setModalCell({ acq: { ...a, versements: arr }, vIndex: vi });
                    }}
                    rows={3}
                    style={{ ...inp, resize: "vertical" }}
                  />
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
                    <button type="button" onClick={() => setModalCell(null)} style={btnSec}>
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const { acq, vIndex: vi } = modalCell;
                        const v = acq.versements[vi];
                        updateVersement(acq.id, vi, {
                          statut: v.statut,
                          montantVerse: Number(v.montantVerse) || 0,
                          dateVersement: v.dateVersement,
                          dateEcheance: v.dateEcheance,
                          notes: v.notes || "",
                        });
                        setModalCell(null);
                      }}
                      style={btnPri}
                    >
                      Enregistrer
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Modal création projet promotion */}
      {showModalProjet && (
        <div style={overlay} onClick={() => setShowModalProjet(false)} role="presentation">
          <div style={{ ...modalBox, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 22 }}>Nouveau projet promotion</h3>
            <label style={lab}>Nom du projet</label>
            <input value={newProject.nom} onChange={(e) => setNewProject((p) => ({ ...p, nom: e.target.value }))} placeholder="Ex: Atlas Bay" style={inp} />
            <label style={lab}>Localisation</label>
            <input value={newProject.localisation} onChange={(e) => setNewProject((p) => ({ ...p, localisation: e.target.value }))} style={inp} />
            <label style={lab}>Nombre total de lots/villas</label>
            <input type="number" value={newProject.nbLots} onChange={(e) => setNewProject((p) => ({ ...p, nbLots: e.target.value }))} style={inp} />
            <label style={lab}>Prix moyen par lot (MAD)</label>
            <input type="number" value={newProject.prixMoyen} onChange={(e) => setNewProject((p) => ({ ...p, prixMoyen: e.target.value }))} style={inp} />
            <label style={lab}>Date de lancement</label>
            <input type="date" value={newProject.dateLancement} onChange={(e) => setNewProject((p) => ({ ...p, dateLancement: e.target.value }))} style={inp} />
            <label style={lab}>Statut</label>
            <select value={newProject.statut} onChange={(e) => setNewProject((p) => ({ ...p, statut: e.target.value }))} style={inp}>
              <option value="en_cours">En cours</option>
              <option value="termine">Terminé</option>
              <option value="pause">En pause</option>
            </select>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button type="button" onClick={() => setShowModalProjet(false)} style={btnSec}>Annuler</button>
              <button type="button" onClick={createPromotionProject} style={btnPri}>Créer le projet</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nouvel acquéreur / modifier */}
      {modalNew && (
        <div
          style={overlay}
          onClick={() => {
            setModalNew(false);
            resetFormAcquereur();
          }}
          role="presentation"
        >
          <div style={{ ...modalBox, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 12px", fontFamily: FF_PLAYFAIR, fontSize: 20 }}>
              {editingAcqId ? "Modifier l'acquéreur" : "Nouvel acquéreur"}
            </h3>
            <label style={lab}>Nom</label>
            <input value={formNew.nom} onChange={(e) => setFormNew((f) => ({ ...f, nom: e.target.value }))} style={inp} />
            <label style={lab}>Prénom</label>
            <input value={formNew.prenom} onChange={(e) => setFormNew((f) => ({ ...f, prenom: e.target.value }))} style={inp} />
            <label style={lab}>N° villa / lot</label>
            <input value={formNew.numVilla} onChange={(e) => setFormNew((f) => ({ ...f, numVilla: e.target.value }))} style={inp} />
            <label style={lab}>Prix de vente (MAD)</label>
            <input
              type="number"
              value={formNew.prixVente}
              onChange={(e) => setFormNew((f) => ({ ...f, prixVente: e.target.value }))}
              style={inp}
            />
            <label style={lab}>Email acquéreur</label>
            <input
              type="email"
              value={formNew.email}
              onChange={(e) => setFormNew((f) => ({ ...f, email: e.target.value }))}
              style={inp}
            />
            <p style={{ fontSize: 12, fontWeight: 700, marginTop: 12 }}>Structure des versements (% — total 100 %)</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
              {formNew.pcts.map((p, i) => (
                <div key={i}>
                  <label style={{ fontSize: 10, color: "#8B7361" }}>V{i + 1}</label>
                  <input
                    type="number"
                    value={p}
                    onChange={(e) => {
                      const next = [...formNew.pcts];
                      next[i] = e.target.value;
                      setFormNew((f) => ({ ...f, pcts: next }));
                    }}
                    style={inp}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                onClick={() => {
                  setModalNew(false);
                  resetFormAcquereur();
                }}
                style={btnSec}
              >
                Annuler
              </button>
              <button type="button" onClick={handleSubmitAcquereur} style={btnPri}>
                {editingAcqId ? "Mettre à jour" : "Ajouter"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

const th = { padding: "10px 8px", textAlign: "left", borderBottom: `2px solid ${BORDER}`, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "#8B7361" };
const td = { padding: "10px 8px", borderBottom: `1px solid ${BORDER}`, verticalAlign: "middle" };

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const modalBox = {
  background: WHITE,
  borderRadius: 12,
  padding: 24,
  maxWidth: 420,
  width: "100%",
  border: `1px solid ${BORDER}`,
  boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
};

const lab = { display: "block", fontSize: 11, fontWeight: 600, color: "#8B7361", marginBottom: 4, marginTop: 8 };
const inp = { width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${BORDER}`, fontFamily: FF_DM, fontSize: 14, boxSizing: "border-box" };
const btnPri = { padding: "10px 18px", border: "none", borderRadius: 8, background: GOLD, color: "#fff", fontWeight: 700, cursor: "pointer" };
const btnSec = { padding: "10px 18px", border: `1px solid ${BORDER}`, borderRadius: 8, background: "#fff", cursor: "pointer" };

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
